import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { InfoCircleIcon } from '@untitled-theme/icons-react'

import { StyledTooltip } from '@/shared/components/StyledTooltip'

import type {
  FundingCell,
  FundingColumn,
  FundingTableRow,
} from '../../types/funding-overview.types'

const HEADER_CELL_SX = {
  fontWeight: 500,
  fontSize: '10px',
  color: '#1A1D24',
  padding: '12px 8px',
  borderBottom: '1px solid #E1E1E4',
  maxWidth: '76px',
}

const BODY_CELL_SX = {
  fontSize: '12px',
  fontWeight: 500,
  color: '#464A54',
  padding: '16px 8px',
  whiteSpace: 'nowrap',
}

const FundingCellContent = ({ cell }: { cell: FundingCell }) => {
  if (cell === null) {
    return <>--</>
  }

  const data = typeof cell === 'string' ? { text: cell } : cell

  if (!data.pill) {
    return <>{data.text}</>
  }

  const pill = (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        backgroundColor: '#E5F5E7',
        padding: '6px 10px',
        borderRadius: '16px',
        fontWeight: 600,
        fontSize: '11px',
        lineHeight: '15px',
        color: '#181D29',
        maxWidth: '124px',
        whiteSpace: 'normal',
      }}
    >
      {data.text}
      {data.tooltip && (
        <InfoCircleIcon style={{ width: 13, height: 13, flexShrink: 0, color: '#464A54' }} />
      )}
    </Box>
  )

  return data.tooltip ? (
    <StyledTooltip title={data.tooltip} placement="bottom">
      {pill}
    </StyledTooltip>
  ) : (
    pill
  )
}

interface FundingTableProps {
  columns: FundingColumn[]
  rows: FundingTableRow[]
}

const FundingTable = ({ columns, rows }: FundingTableProps) => {
  return (
    <TableContainer
      sx={{
        border: '1px solid #E1E1E4',
        borderRadius: '16px',
        overflow: 'auto',
      }}
    >
      <Table sx={{ tableLayout: 'auto', width: '100%', minWidth: '640px' }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#FAFAFB' }}>
            <TableCell sx={{ ...HEADER_CELL_SX, maxWidth: 'none', paddingLeft: '16px' }}>
              Einzelmaßnahme
            </TableCell>
            {columns.map(column => (
              <TableCell key={column.label} sx={HEADER_CELL_SX}>
                {column.tooltip ? (
                  <StyledTooltip title={column.tooltip} placement="bottom">
                    <Box component="span" sx={{ cursor: 'default' }}>
                      {column.label}
                    </Box>
                  </StyledTooltip>
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => {
            const isLastRow = rowIndex === rows.length - 1
            const borderBottom = isLastRow ? 'none' : '1px solid #E1E1E4'

            return (
              <TableRow key={`${row.measure}-${row.measureSubtitle ?? rowIndex}`}>
                <TableCell sx={{ ...BODY_CELL_SX, borderBottom, paddingLeft: '16px' }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '13px',
                      maxWidth: '180px',
                      whiteSpace: 'normal',
                    }}
                  >
                    {row.measure}
                  </Typography>
                  {row.measureSubtitle && (
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: '10px',
                        color: '#6A6E78',
                        maxWidth: '180px',
                        whiteSpace: 'normal',
                      }}
                    >
                      {row.measureSubtitle}
                    </Typography>
                  )}
                </TableCell>
                {row.cells.map((cell, cellIndex) => (
                  <TableCell
                    key={columns[cellIndex]?.label ?? cellIndex}
                    sx={{ ...BODY_CELL_SX, borderBottom }}
                  >
                    <FundingCellContent cell={cell} />
                  </TableCell>
                ))}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default FundingTable
